import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Waves } from "lucide-react";

const Privacy = () => {
  useEffect(() => {
    document.title = "Privacy Policy — Barrelcast";
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border py-5 px-6">
        <Link to="/" className="inline-flex items-center gap-2 text-primary font-bold text-sm uppercase tracking-widest hover:opacity-80 transition-opacity">
          <Waves className="h-4 w-4" /> Barrelcast
        </Link>
      </header>

      {/* Content */}
      <main className="container max-w-3xl py-16 px-6">
        <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-10">Last updated: May 2026</p>

        <div className="space-y-10 text-sm leading-7 text-foreground/80">

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">1. Introduction</h2>
            <p>
              Barrelcast ("we", "us", "our") is committed to protecting your personal information in
              accordance with the Protection of Personal Information Act 4 of 2013 (POPIA) of South
              Africa. This Privacy Policy explains what data we collect, how we use it, and your rights
              regarding your personal information.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">2. Information We Collect</h2>
            <p>We may collect the following categories of personal information:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li><strong className="text-foreground">Account information</strong> — name and any details you provide when creating a profile</li>
              <li><strong className="text-foreground">Location data</strong> — approximate GPS coordinates, only if you grant permission, used solely to calculate distance to surf breaks and provide directions</li>
              <li><strong className="text-foreground">Usage data</strong> — pages visited, features used, and device/browser type, collected anonymously to improve the Service</li>
              <li><strong className="text-foreground">Preferences</strong> — your selected surf breaks, unit preferences (km/mi), and board settings stored locally on your device</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">3. How We Use Your Information</h2>
            <p>We use your personal information to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Provide and improve the Barrelcast Service</li>
              <li>Maintain and manage your user profile</li>
              <li>Calculate distances and provide directions to surf breaks</li>
              <li>Analyse usage patterns to improve performance and features</li>
              <li>Respond to support queries or feedback</li>
            </ul>
            <p className="mt-3">
              We do not sell, rent, or trade your personal information to third parties.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">4. Location Data</h2>
            <p>
              Barrelcast requests access to your device's location solely to show your distance from surf
              breaks and generate driving directions via Google Maps. Location access is optional — the
              Service functions fully without it. We do not store or transmit your location data to our
              servers. Location is processed in your browser session only.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">5. Third-Party Services</h2>
            <p>Barrelcast uses the following third-party services which may process data independently:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li><strong className="text-foreground">Open-Meteo</strong> — marine and weather forecast API (no personal data shared)</li>
              <li><strong className="text-foreground">Google Maps</strong> — used for directions when you opt in; subject to Google's own Privacy Policy</li>
              <li><strong className="text-foreground">Vercel</strong> — hosting provider that may collect standard server logs</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">6. Cookies & Local Storage</h2>
            <p>
              Barrelcast may use browser local storage to save your preferences (such as selected break
              and unit settings) between sessions. We do not use advertising cookies or third-party
              tracking cookies. Standard analytics may use first-party cookies to measure usage
              anonymously.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">7. Data Retention</h2>
            <p>
              Profile data is retained for as long as your account is active. You may request deletion
              of your account and associated data at any time. Anonymous usage data may be retained in
              aggregated form for analytical purposes.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">8. Your Rights under POPIA</h2>
            <p>As a data subject under POPIA, you have the right to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Be informed about how your personal information is used</li>
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate personal information</li>
              <li>Request deletion of your personal information</li>
              <li>Object to the processing of your personal information</li>
              <li>Lodge a complaint with the Information Regulator of South Africa</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, contact us via{" "}
              <a href="https://barrelcast.surf" className="text-primary underline hover:opacity-80">
                barrelcast.surf
              </a>.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">9. Children's Privacy</h2>
            <p>
              Barrelcast is not directed at children under the age of 13. We do not knowingly collect
              personal information from children. If you believe a child has provided us with personal
              information, please contact us and we will delete it promptly.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">10. Security</h2>
            <p>
              We take reasonable technical and organisational measures to protect your personal
              information against unauthorised access, loss, or misuse. However, no internet transmission
              is completely secure and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">11. Changes to this Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of significant
              changes by updating the date at the top of this page. Continued use of the Service after
              changes are posted constitutes acceptance of the revised Policy.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">12. Contact & Information Regulator</h2>
            <p>
              For privacy-related queries, contact us at{" "}
              <a href="https://barrelcast.surf" className="text-primary underline hover:opacity-80">
                barrelcast.surf
              </a>.
            </p>
            <p className="mt-3">
              You also have the right to lodge a complaint with the Information Regulator of South Africa:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Website: <a href="https://inforegulator.org.za" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:opacity-80">inforegulator.org.za</a></li>
              <li>Email: inforeg@justice.gov.za</li>
            </ul>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 text-center text-sm text-gray-300">
        <p>Made with salt &amp; stoke. Always check local conditions before paddling out.</p>
        <p className="mt-1">© Copyright 2026 Barrelcast. All rights reserved.</p>
        <p className="mt-2 flex justify-center gap-4">
          <Link to="/terms" className="underline hover:text-white transition-colors">Terms &amp; Conditions</Link>
          <Link to="/privacy" className="underline hover:text-white transition-colors">Privacy Policy</Link>
        </p>
      </footer>
    </div>
  );
};

export default Privacy;
